import { Input } from "@amzn/awsui-components-react";
import React from "react";
import CONSTANTS from "src/utils/constants";
import WorksheetTotal from "src/models/tp-allocation/WorksheetTotal";
import ATPTextAreaAutoComplete, { AutoSuggesstListItem } from "src/components/shared/ATPTextAreaAutoComplete";

/** A Step 2 grid row: a total plus the display values the grid adds. */
export type DasTotalRow = WorksheetTotal & { errorIndicator?: React.ReactNode };

export interface DasTotalsEditorsData {
    totalsList: WorksheetTotal[];
    suggestionsList: AutoSuggesstListItem[];
    onTextAreaCaretPositionChanged: (position: number) => void;
    shouldShowValues?: boolean;
}

export interface DasTotalsCellProps {
    value?: string;
    row: { index: number; original: DasTotalRow };
    column: { id: string };
    onRowUpdated: (rowIndex: number, columnId: string, value?: string) => void;
    editorsData: DasTotalsEditorsData;
}

/**
 * Cell renderer of the Step 2 grid: the formula editor and name input for the row being edited, plain text
 * otherwise. Adapted from the TPA totals grid; keep the two in sync.
 */
export const DasTotalsEditableCell = ({
    value: initialValue,
    row: { index, original },
    column: { id },
    onRowUpdated,
    editorsData,
}: DasTotalsCellProps) => {
    const [value, setValue] = React.useState(initialValue);

    React.useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    const onTextAreaBlur = (e: { textAreaValue: string }) => {
        onRowUpdated(index, id, e.textAreaValue);
    }

    const onBlur = () => {
        onRowUpdated(index, id, value)
    }

    const onChange = ({ detail }: { detail: { value: string } }) => {
        setValue(detail.value);
    }

    const total = original;
    const allTotals = editorsData.totalsList;

    if (total.isBeingEdited){
        switch (id) {
            case CONSTANTS.WORKSHEET_TOTAL_FIELDS.OVERVIEW: {
                const totalsAbove = allTotals.filter(x => parseFloat(x.visibleSequence || '0') < parseFloat(total.visibleSequence || '0'))
                    .map(x => ({name: `S${x.visibleSequence}.${x.description}`, value: x.description, type: 'Previous Totals'}))
                const editorData = { ...editorsData, suggestionsList: totalsAbove.concat(editorsData.suggestionsList) };
                return <ATPTextAreaAutoComplete 
                            key={`${id}_${index}`}
                            editorsData={editorData}
                            initialValue={initialValue ?? ''}
                            onTextAreaBlur={onTextAreaBlur} />    
            }
            case CONSTANTS.WORKSHEET_TOTAL_FIELDS.DESCRIPTION:
                return <div key={`${id}_${index}`} className={`save-status-${total.savingStatus} cell-text ${id}Cell`}>
                    <Input key={`${id}_${index}`} className={"dataSourcesButton " + id + "Editor" + original.totalId} placeholder="Enter value" value={value ?? ''} onChange={onChange} onBlur={onBlur} />
                    {original.errorIndicator && <span className="inlineErrorContainer">{original.errorIndicator}</span>}
                </div>;
            default:
                return <Input key={`${id}_${index}`} className={"dataSourcesButton " + id + "Editor" + original.totalId} placeholder="Enter value" value={value ?? ''} onChange={onChange} onBlur={onBlur} />;
        }
    }
    else {
        switch (id) {
            case CONSTANTS.WORKSHEET_TOTAL_FIELDS.DESCRIPTION:
                return <div key={`${id}_${index}`} className={`save-status-${total.savingStatus} cell-text ${id}Cell`}>
                    <span>{value}</span>
                    <div className="inlineErrorContainer">{original.errorIndicator && <div className="inlineErrorContainer">{original.errorIndicator}</div>}</div>
                </div>;
            case CONSTANTS.WORKSHEET_TOTAL_FIELDS.OVERVIEW: {
                return <div key={`${id}_${index}`} className={`save-status-${total.savingStatus}`}>
                        {total.expression == null || total.expression === ""? "Click to enter TP calculation formula" : total.expressionFormatted}
                        {editorsData.shouldShowValues && total.outputBreakdown? <div className="greyBadge">{total.outputBreakdown}</div> : null }
                    </div>;
            }
        }
    }
    return <div key={`${id}_${index}`} className={`save-status-${total.savingStatus} cell-text ${id}Cell`}>{value}</div>;
};

export const dasTotalsCellEditor = {
    Cell: DasTotalsEditableCell,
};

export const DAS_TOTAL_MENU_ACTIONS = {
    EDIT: { text: "Edit", id: "Edit" },
    DUPLICATE: { text: "Duplicate", id: "Duplicate" },
    REMOVE: { text: "Remove", id: "Remove" },
}

export const DAS_TOTAL_MENU_ITEMS = [
    DAS_TOTAL_MENU_ACTIONS.EDIT,
    DAS_TOTAL_MENU_ACTIONS.DUPLICATE,
    DAS_TOTAL_MENU_ACTIONS.REMOVE
];
